import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import GhxpArrows from "./GhxpArrows";

const TYPEAHEAD_MS = 600;

// Seleção no padrão "combobox só de seleção" (WAI-ARIA APG), no lugar do <select> nativo, cuja lista aberta
// é desenhada pelo sistema e não aceita estilo. O foco fica no botão; a opção ativa vai por aria-activedescendant.
// Teclado: ↓/↑/Enter/Espaço abrem; ↓/↑/Home/End/PageUp/PageDown navegam; Enter/Espaço escolhem; Esc fecha;
// Tab escolhe a opção ativa e segue; digitar o início de uma opção ("5") pula até ela.
// A lista abre para cima quando não cabe abaixo do campo.
function GhxpSelect({ id, name, value, options, placeholder, invalid, describedBy, labelId, onChange }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [up, setUp] = useState(false);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const typeRef = useRef({ text: "", at: 0 });

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selected = options[selectedIndex];
  const listId = `${id}-list`;
  const optionId = (i) => `${id}-opt-${i}`;

  const openList = (index = selectedIndex >= 0 ? selectedIndex : 0) => {
    setActive(index);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setActive(-1);
  };

  const choose = (index) => {
    if (index >= 0 && options[index] && options[index].value !== value) onChange(options[index].value);
    close();
  };

  // Abre para cima se o espaço abaixo (dentro da área rolável do slide) não comporta a lista.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !listRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const area = buttonRef.current.closest("[data-scrollable]");
    const bounds = area ? area.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const bottom = Math.min(bounds.bottom, window.innerHeight);
    const top = Math.max(bounds.top, 0);
    const needed = listRef.current.scrollHeight + 12;
    setUp(bottom - rect.bottom < needed && rect.top - top > bottom - rect.bottom);
  }, [open]);

  // Mantém a opção ativa visível quando a lista rola (telas baixas). Sem scrollIntoView: ele também
  // rolaria os contêineres com overflow: hidden do slide.
  useEffect(() => {
    const list = listRef.current;
    const item = open && active >= 0 ? list?.children[active] : null;
    if (!item) return;
    if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
    else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [open, active]);

  // Toque ou clique fora fecha (o Safari não foca botões no toque, então não dá para depender do blur).
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) close();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  // O slide deixa de ser o atual com a lista aberta (troca de slide pelo trilho, rolagem): fecha.
  useEffect(() => {
    if (!open) return undefined;
    const slide = rootRef.current?.closest("[data-slide-index]");
    if (!slide) return undefined;
    const observer = new MutationObserver(() => !slide.classList.contains("is-active") && close());
    observer.observe(slide, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [open]);

  const typeahead = (key) => {
    const now = Date.now();
    const text = now - typeRef.current.at > TYPEAHEAD_MS ? key : typeRef.current.text + key;
    typeRef.current = { text, at: now };
    const start = open ? active : selectedIndex;
    const order = options.map((_, i) => (start + 1 + i) % options.length);
    const match = order.find((i) => options[i].label.toLowerCase().startsWith(text.toLowerCase()));
    if (match === undefined) return;
    if (open) setActive(match);
    else if (options[match].value !== value) onChange(options[match].value);
  };

  const onKeyDown = (event) => {
    const { key } = event;
    const last = options.length - 1;
    const handled = () => event.preventDefault();

    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(key)) {
        handled();
        openList(key === "ArrowUp" && selectedIndex < 0 ? last : undefined);
      } else if (key === "Home" || key === "End") {
        handled();
        openList(key === "Home" ? 0 : last);
      } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        handled();
        typeahead(key);
      }
      return;
    }

    switch (key) {
      case "ArrowDown":
        handled();
        setActive((i) => Math.min(last, i + 1));
        break;
      case "ArrowUp":
        handled();
        if (event.altKey) choose(active);
        else setActive((i) => Math.max(0, i - 1));
        break;
      case "Home":
      case "PageUp":
        handled();
        setActive(0);
        break;
      case "End":
      case "PageDown":
        handled();
        setActive(last);
        break;
      case "Enter":
      case " ":
        handled();
        choose(active);
        break;
      case "Escape":
        handled();
        close();
        break;
      case "Tab":
        choose(active);
        break;
      default:
        if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          handled();
          typeahead(key);
        }
    }
  };

  return (
    <div ref={rootRef} className={`ghxpSelect ${open ? "is-open" : ""} ${up ? "is-up" : ""} ${selected ? "has-value" : ""}`}>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        name={name}
        className="ghxpSelect__button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={labelId}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
        onBlur={(event) => {
          if (open && !rootRef.current?.contains(event.relatedTarget)) close();
        }}
      >
        <span className="ghxpSelect__value">{selected ? selected.label : placeholder}</span>
        <svg className="ghxpSelect__chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M3 6l5 5 5-5" />
        </svg>
      </button>

      {/* mousedown sem efeito padrão: o foco não sai do botão ao clicar numa opção. */}
      <ul
        ref={listRef}
        id={listId}
        className="ghxpSelect__list"
        role="listbox"
        aria-labelledby={labelId}
        tabIndex={-1}
        data-scrollable="y"
        onMouseDown={(event) => event.preventDefault()}
      >
        {options.map((option, i) => (
          <li
            key={option.value}
            id={optionId(i)}
            role="option"
            aria-selected={i === selectedIndex}
            className={`ghxpSelect__option ${i === active ? "is-active" : ""}`}
            style={{ "--i": i }}
            onClick={() => choose(i)}
            onMouseMove={() => active !== i && setActive(i)}
          >
            <span>{option.label}</span>
            {i === selectedIndex && <GhxpArrows variant="solid" size="sm" className="ghxpSelect__check" />}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default GhxpSelect;
